import { computed } from 'vue'
import { useTranslate } from "@zotlo-shared/composable/useTranslate";
import { getDocumentLinks } from '@zotlo-shared/utils/getDocumentLinks';
import { DOCS } from '@zotlo-shared/types';
import { getPackagePrices, getPackageTypeConditions } from '@zotlo-shared/elements/common/payment/utils';

export function useApplyLinks(props: Record<string, any>) {
  const { translate, $te } = useTranslate();

  const RUSSIA_DOCUMENTS = {
    payment_aggregator: 'https://3p-assets.cdnztl.com/docs/2025/09/10/jigle-payment-terms-ru.pdf',
  }

  const packagePricesData = computed(() => getPackagePrices(props.pageData?.selectedPackage, props.locale));
  const packageTypeData = computed(() => getPackageTypeConditions(props.pageData?.selectedPackage, { isTrialUsed: props.pageData?.isTrialUsed }));

  const linksByAttr = computed(() => {
    const countryCode = props.pageData?.countryCode;
    const isWebDeeplinkActive = !!props.pageData?.deeplinkSettings?.deeplinkWebStatus;

    const docLinks = getDocumentLinks({
      countryCode: countryCode?.toLowerCase() ?? 'en',
      lang: props.locale,
      links: props.pageData?.docLinks || {}
    });

    return {
      "data-privacy-policy-link": props.pageData?.applicationLinks?.privacyUrl,
      "data-terms-of-use-link": props.pageData?.applicationLinks?.termsOfServiceUrl,
      "data-customer-service-link": props.pageData?.applicationLinks?.customerSupportUrl,
      "data-generic-app-link": props.pageData?.applicationLinks?.[isWebDeeplinkActive ? 'deeplinkWeb' : 'genericDownloadUrl'],
      "data-zotlo-privacy-policy-link": docLinks?.[DOCS.PRIVACY_POLICY],
      "data-zotlo-terms-of-use-link": docLinks?.[DOCS.TERMS_OF_SERVICE],
      "data-payment-aggregator": RUSSIA_DOCUMENTS.payment_aggregator,
    }
  });

  const params = computed(() => {
    const isRussia = props.pageData?.countryCode === 'RU';
    const statementName = props.isEditorMode ? '{statementName}' : props.pageData?.statementName || '';
    const appName = props.isEditorMode ? '{appName}' : props.pageData?.appName || '';

    const oneTimePaymentInfo = packageTypeData.value.isOneTimePayment ? `<p>${translate('generate.legalDocInfoElement.oneTimePaymentInfo', props.locale, { price: (packagePricesData.value as Record<string, any>).price })}</p><br/>` : '';
    const aggreementInfo = `<p>${translate('generate.legalDocInfoElement.agreementInfo', props.locale, { appName })}</p><br/>`
    const aggrementMoreInfo = `<p>${translate('generate.legalDocInfoElement.agreementMoreInfo', props.locale, { appName })}</p><br/>`
    const chargeStatement = `<p>${translate('generate.legalDocInfoElement.chargeStatement', props.locale, { statementName })}</p><br/>`
    const aggregatorLine = isRussia ? `<p>${translate('generate.legalDocInfoElement.expandedRussiaAdditional', props.locale)}</p><br/>` : '';
    const addressLine = isRussia ? '' : `<p>${translate('generate.legalDocInfoElement.zotloAddress', props.locale)}</p>`;

    return {
      oneTimePaymentInfo,
      aggreementInfo,
      aggrementMoreInfo,
      chargeStatement,
      aggregatorLine,
      addressLine,
      ...props.element.options?.params
    }
  });

  const textValue = computed(() => {
    const isThereAnyParam = props.value.includes('{') && props.value.includes('}');
    const isValidKey = $te(props.value);
    const translated = isValidKey || isThereAnyParam ? translate(props.value, props.locale, params.value) : props.value;

    return props.isEditorMode
      ? translated
      : Object.entries(linksByAttr.value).reduce((acc, [key, value]) => {
          return acc.replace(new RegExp(key, 'g'), `${key} href="${value || '#'}" target="_blank"`)
        }, translated)
  });

  const linksAttrs = computed(() => {
    if (props.isEditorMode || props.element.tagName !== 'a') return {};

    return Object.entries(props.element.attr)
      .filter(([key]) => /^data-/.test(key))
      .reduce((acc, item) => {
        const key = item[0] as keyof typeof linksByAttr.value;
        const val = linksByAttr.value[key];
        if (val) {
          acc['href'] = val || '#';
          acc['target'] = '_blank'
        }
        return acc;
      }, {} as Record<string, any>)
  })

  return {
    textValue,
    linksAttrs,
  }
}
